import { defineStore } from 'pinia';
import { ref, computed, watch } from 'vue';
import { useWorkspacesStore } from "stores/workspaces";
import { useAccountStore } from "stores/account";
import api from '../services/api'
import {Mutex, Semaphore, withTimeout} from 'async-mutex';
import { LoadingBar } from 'quasar'
import { isSameDay, format, isValid } from 'date-fns'

const initAnalyticsApiMutex = new Mutex(); // TODO: we can use 2 mutex, one for account and one for workspace. Maybe not so critical

LoadingBar.setDefaults({
  color: 'blue',
  size: '5px',
  position: 'top'
})

// TODO: (1) generate another token from the API which is scoped to workspace (2) use workspaces store to compute current workspace, and load only the data of the current store

// Using factory and proxy for this store, so I can use watch from inside the store itself.
// Code snippet from here: https://github.com/vuejs/pinia/discussions/794#discussioncomment-3709809
const analyticsStoreFactory = defineStore('analytics', () => {
  const accountStore = useAccountStore()
  const workspacesStore = useWorkspacesStore()
  const accountUsage = ref({
    submissions: 0,
    spam: 0,
    /** @type {Record<workspaceId.string, Object<submissions.number, spam.number>>} **/
    submissionsPerWorkspace: {},
    loaded: false,
    /** @type {Date} ***/
    cycleStart: null,
    /** @type {Date} ***/
    cycleEnd: null
  })
  const analyticsAccountApi = ref({
    token: null,
    analyticsUri: null,
    expires: 4102444799 // 2099-12-31T23:59:59Z
  })
  const analyticsWorkspaceApi = ref({
    token: null,
    analyticsUri: null,
    expires: 4102444799 // 2099-12-31T23:59:59Z
  })

  const workspaceData = ref({
    timerange: {
      from: null,
      to: null
    },
    dates: [],
    loading: true,
    data: []
  })

  function $reset() {
    accountUsage.value = {
      submissions: 0,
      spam: 0,
      badge: 0,
      loaded: false,
      cycleStart: null,
      cycleEnd: null
    }
    analyticsAccountApi.value = {
      token: null,
      analyticsUri: null,
      expires: 4102444799 // 2099-12-31T23:59:59Z
    }
    workspaceData.value = {
      timerange: {
        from: null,
        to: null
      },
      dates: [],
      loading: true,
      data: []
    }
  }
  const workspaceId = computed(() => {
    return workspacesStore.active?.id
  })
  // const analyticsApi = computed(() => {
  //   if (!accountId.value) {
  //     return {
  //       token: null,
  //       analyticsUri: null,
  //       expires: 4102444799 // 2099-12-31T23:59:59Z}
  //     }
  //   }
  // })
  async function reload() {
    // I think we don't need anything here, because data is loaded when workspace is set
  }

  /**
   *
   * @param {'ACCOUNT'|'WORKSPACE'} mode
   * @return {Promise<void>}
   */
  async function initAnalyticsApi(mode) {
    const analyticsApi = mode === 'ACCOUNT' ? analyticsAccountApi : analyticsWorkspaceApi
    const release = await initAnalyticsApiMutex.acquire();
    try {
      console.log('initializeing analyticsApi', mode)
      if (analyticsApi.value?.token && new Date().getTime() / 1000 < analyticsApi.value.expires) {
        console.log('analyticsApi already initialized. returning.')
        return
      }
      if (mode === 'ACCOUNT') {
        await accountStore.loadAccount(false)
        const accountId =  accountStore.account.id
        let token = await api.getAnalyticsToken(accountId);
        analyticsApi.value = token || analyticsApi.value
      } else {
        await workspacesStore.loadWorkspaces(false)
        let token = await api.getAnalyticsToken(null, workspaceId.value);
        analyticsApi.value = token || analyticsApi.value
      }
    } finally {
      release()
    }
  }


  async function loadWorkspaceData() {
    await initAnalyticsApi.call(this, 'WORKSPACE')
    if (!workspaceData.value.timerange.from || !workspaceData.value.timerange.to) {
      return
    }
    workspaceData.value.loading = true
    // Loading.show()
    LoadingBar.start()
    const res = await fetch(analyticsWorkspaceApi.value.analyticsUri, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: analyticsWorkspaceApi.value.token
      },
      body: JSON.stringify({
        "timeframe": {
          "from": workspaceData.value.timerange.from.toISOString(),
          "to": workspaceData.value.timerange.to.toISOString()
        },
        "timezone": Intl.DateTimeFormat().resolvedOptions().timeZone,
        "aggregate": "1d",
        "select": [
          {
            "dimension": "spam",
            "aggregator": "group"
          },
          {
            "dimension": "badge",
            "aggregator": "group"
          },
          {
            "dimension": "formId",
            "aggregator": "group"
          },
          {
            "dimension": "workspaceId",
            "aggregator": "group"
          },
          {
            "measurement": "files",
            "aggregator": "sum",
            "alias": "sum_files"
          }
        ]
      })
    })
    if (res.ok) {
      const json = await res.json()
      workspaceData.value.data = json.data
      workspaceData.value.loading = false
    }
    setTimeout(() => {
      // Loading.hide()
      LoadingBar.stop()
    }, 10)
  }

  async function loadAccountAnalytics(refresh) {
    if (accountUsage.value.loaded && !refresh) {
      return
    }
    await initAnalyticsApi.call(this, 'ACCOUNT')
    const end = 'now'
    const start = '-30d'
    // start.setMonth(start.getMonth() - 1)
    // start.setHours(0,0,0,0)

    // const cycleStart = new Date()
    // // cycleStart.setUTCDate(1)
    // cycleStart.setDate(1)
    // // cycleStart.setUTCHours(0,0,0,0)
    // cycleStart.setHours(0,0,0,0)
    // const cycleEnd = new Date(cycleStart)
    // cycleEnd.setMonth(cycleStart.getMonth() + 1)
    // accountUsage.value.cycleStart = cycleStart
    // accountUsage.value.cycleEnd = cycleEnd
    const aggregationsTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const res = await fetch(analyticsAccountApi.value.analyticsUri, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: analyticsAccountApi.value.token
      },
      body: JSON.stringify({
        "timeframe": {
          "from": start, // cycleStart.toISOString(),
          "to": end // cycleEnd.toISOString()
        },
        "timezone": aggregationsTimezone,
        "aggregate": "1d",
        "select": [
          {
            "dimension": "workspaceId",
            "aggregator": "group"
          },
          {
            "dimension": "spam",
            "aggregator": "group"
          }
        ]
      })
    })
    if (res.ok) {
      const json = await res.json()
      let submissions = 0
      let spam = 0
      let workspaces = {}
      const from = new Date(json.timeRange.from)
      const to = new Date(json.timeRange.to)
      accountUsage.value.cycleStart = from
      accountUsage.value.cycleEnd = to
      const cycleStartInMillis = from.getTime() // cycleStart.getTime()
      json.data.forEach(d => {
        const groupStartTime = Date.parse(d._time) // d._time is in local date/time format and therefore interpreted same as aggregationTimezone
        if (groupStartTime < cycleStartInMillis) {
          // the analytics engine sometimes return extra buckets of time before the requested time, we filter them out
          return
        }
        const workspaceId = d.workspaceId || '[No workspace]'
        if (!workspaces[workspaceId]) {
          workspaces[workspaceId] = {submissions: 0, spam:0}
        }
        if(d.spam) {
          spam += d._count
          workspaces[workspaceId].spam += d._count
        } else {
          submissions += d._count
          workspaces[workspaceId].submissions += d._count
        }
      })
      // const submissionsPerWorkspace = []
      // Object.keys(workspaces).forEach(workspaceId => {
      //   submissionsPerWorkspace.push({
      //     workspaceId,
      //     submissions: workspaces[workspaceId].submissions,
      //     spam: workspaces[workspaceId].spam
      //   })
      // })
      accountUsage.value.submissions = submissions
      accountUsage.value.spam = spam
      accountUsage.value.submissionsPerWorkspace = workspaces
      accountUsage.value.dailyStats = json.data
      accountUsage.value.loaded = true
    }
  }


  return {
    $reset,
    accountUsage,
    workspaceData,
    loadAccountAnalytics,
    loadWorkspaceData
  }
});

let store = null;
export const useAnalyticsStore = new Proxy(analyticsStoreFactory, {
  apply: (target, thisArg, argumentsList) => {
    if(!store) {
      store = target.apply(thisArg, argumentsList);

      watch([() => store.workspaceData.timerange.from, () => store.workspaceData.timerange.to],
        async () => {
          if (!isValid(store.workspaceData.timerange.from) || !isValid(store.workspaceData.timerange.to)) {
            return
          }
          let d = new Date(store.workspaceData.timerange.from)
          let dates = []
          while (!isSameDay(d, store.workspaceData.timerange.to)) {
            dates.push(format(d, 'yyyy-MM-dd'))
            d.setDate(d.getDate() + 1)
          }
          dates.push(format(d, 'yyyy-MM-dd')) // add the 'to' date
          store.workspaceData.dates = dates
          await store.loadWorkspaceData()
        })
    }

    return store;
  }
});


